import { useCallback, useMemo } from "react"
import { useMediaQuery } from "@react-hookz/web"
import { useTranslation } from "react-i18next"
import { v4 as uuid } from "uuid"
import DriverOverviewBar from "@/components/chat/DriverOverviewBar"
import { useDriverOverviewRoute } from "@/components/chat/useDriverOverviewRoute"
import {
  type OrderDocument,
  useOrderDocuments,
} from "@/components/chat/useOrderDocuments"
import { useOrderStatusLink } from "@/components/chat/useOrderStatusLink"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { useChatTachos } from "@/hooks/useChatTachos"
import useIsReadOnlyMap from "@/hooks/useIsReadOnlyMap"
import { useMinuteClock } from "@/hooks/useMinuteClock"
import { useShareRoute } from "@/hooks/useShareRoute"
import { useStopToStopDistance } from "@/hooks/useStopToStopDistance"
import { useWidgetTabs } from "@/hooks/useWidgetTabs"
import { driverOverview } from "@/lib/driverOverview"
import { chatWorkTime, leadWorkTime } from "@/lib/workTime"
import { findActivity } from "@/reducers/activitySlice"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { Location } from "@/reducers/locationsSlice"
import { Message } from "@/reducers/messagesSlice"
import { updateImageGallery, updatePDFViewer } from "@/reducers/overlaysSlice"
import { selectReferenceRouteStatus } from "@/reducers/referenceRouteStatusesSlice"
import { selectIsRoomSyncFinished } from "@/reducers/roomSyncStatusSelectors"
import { findRoute2g, findRouteConfig } from "@/reducers/routes2gSlice"
import { selectAccountHasTacho } from "@/reducers/tachosSliceSelectors"
import { isMobile } from "@/utils/detectDevice"

const IMAGE_FILE = /\.(gif|png|jpe?g|webp)$/i

const DriverOverviewSection = ({
  chatJid,
  routeJid,
  location,
}: {
  chatJid: string
  routeJid: string | undefined
  location: Location | undefined
}) => {
  const { t } = useTranslation()
  const now = useMinuteClock()
  const route = useAppSelector(useMemo(() => findRoute2g(routeJid), [routeJid]))
  const activity = useAppSelector(
    useMemo(() => findActivity(routeJid), [routeJid]),
  )
  const routeAvoid = useAppSelector(
    useMemo(() => findRouteConfig(routeJid), [routeJid]),
  )?.avoid
  const referenceRouteStatus = useAppSelector(
    useMemo(() => selectReferenceRouteStatus(routeJid), [routeJid]),
  )
  const syncFinished = useAppSelector(selectIsRoomSyncFinished(chatJid))
  const accountHasTacho = useAppSelector(selectAccountHasTacho)
  const { tachos, isDoubleCrew } = useChatTachos(chatJid)
  const { openTabOnTop, addTab } = useWidgetTabs()
  const routeEditing = useDriverOverviewRoute(routeJid, route)
  const shareRoute = useShareRoute(route, routeJid ?? "")
  const readOnlyMap = useIsReadOnlyMap(chatJid)
  const isSmallScreen = useMediaQuery("(max-width: 900px)")
  const canSendRoute =
    !isMobile &&
    !isSmallScreen &&
    !readOnlyMap &&
    (route?.stops?.length ?? 0) > 0
  const documents = useOrderDocuments(chatJid)
  const { orderStatus, dialog: orderDetailsDialog } = useOrderStatusLink(
    routeJid,
    route?.stops,
  )
  const dispatch = useAppDispatch()

  const openDocument = useCallback(
    (document: OrderDocument) => {
      const url = mapFileUrlToSameOrigin(document.url)
      dispatch(
        IMAGE_FILE.test(url)
          ? updateImageGallery({ visible: true, directImage: { src: url } })
          : updatePDFViewer({
              visible: true,
              PDFMessage: {
                id: `order-document-${uuid()}`,
                body: "",
                url,
                file: true,
                from: chatJid,
                to: chatJid,
                type: "groupchat",
                markers: [],
                reactions: [],
                timestamp: new Date().toISOString(),
              } as unknown as Message,
            }),
      )
    },
    [dispatch, chatJid],
  )

  const showLegDistance = useStopToStopDistance()
  const overview = useMemo(
    () =>
      driverOverview({
        route,
        activity,
        referenceRouteStatus,
        syncFinished,
        accountHasTacho,
        workTime: leadWorkTime(
          chatWorkTime(
            tachos.map(({ tacho }) => tacho),
            isDoubleCrew,
            now,
          ),
        ),
        location,
        now,
        showLegDistance,
        routeAvoid,
        t,
      }),
    [
      route,
      activity,
      referenceRouteStatus,
      syncFinished,
      accountHasTacho,
      tachos,
      isDoubleCrew,
      location,
      now,
      showLegDistance,
      routeAvoid,
      t,
    ],
  )

  if (overview.kind === "hidden") return null
  return (
    <div className="relative z-20">
      <DriverOverviewBar
        className="mx-4 mb-2 mt-1"
        overview={overview}
        onOpenMap={() => openTabOnTop("map")}
        onOpenWorkTime={() => addTab("workTime")}
        documents={documents}
        onOpenDocument={openDocument}
        orderStatus={orderStatus}
        routeEditing={
          routeJid
            ? {
                driverJid: routeJid,
                updating: routeEditing.updating,
                pendingAdds: routeEditing.pendingAdds,
                pendingStops: routeEditing.pendingStops,
                onReorder: routeEditing.reorder,
                onAdd: routeEditing.add,
                onChangeAction: routeEditing.changeAction,
                onChangeLocation: routeEditing.changeLocation,
                onChangeAvoidTolls: routeEditing.changeAvoidTolls,
                onRemove: routeEditing.remove,
                tolls: routeEditing.tolls.tolls,
                tollsVisible: routeEditing.tolls.visible,
                tollsLoading: routeEditing.tolls.loading,
                tollsLabel: routeEditing.tolls.label,
                onToggleTolls: routeEditing.tolls.toggle,
                sendRoute: canSendRoute
                  ? {
                      label: shareRoute.isDoubleCrew
                        ? t("chat.driverOverview.sendToDrivers")
                        : t("chat.driverOverview.sendToDriver"),
                      sending: shareRoute.sharing,
                      onSend: () => {
                        addTab("chat")
                        void shareRoute.share()
                      },
                    }
                  : undefined,
              }
            : undefined
        }
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-full h-6 bg-gradient-to-b from-sidebar to-sidebar/0"
        aria-hidden
      />
      {orderDetailsDialog}
    </div>
  )
}

export default DriverOverviewSection
